import type { TripRoute } from "@/views/trips/edit/trip";
import { type MaybeRefOrGetter, toValue } from "vue";
import type { DisabledRule } from "@/types/validation";
import { OrderStopType, OrderStopTypeMap } from "@/data/order";
import { isSameEntity } from "@/utils/sameEntity";
import type { RouteStopDto } from "@/api/trips/dto/trip.dto";
import { RouteStatus, RouteStopStatus } from "@/data/trip";
import i18n from "@/plugins/i18n";
import { TravelOrderPaymentTypeLabel } from "@/data/travelOrder";
import { useBaseView } from "@/composables";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";

const t = i18n.global.t;

export function useDisableRules() {
  function createTripDispatchDisableRules(
    routeOrRoutes: MaybeRefOrGetter<TripRoute[] | TripRoute>,
    dispatchRequired = false,
  ): DisabledRule[] {
    const raw = toValue(routeOrRoutes);

    const routes: TripRoute[] = Array.isArray(raw) ? raw : [raw];

    if (!routes.length) {
      return [];
    }

    function getRoutePosition(route: TripRoute | undefined): string {
      return route ? (routes.findIndex((item) => isSameEntity(item, route)) + 1).toString() : "";
    }

    const travelOrders = routes.flatMap((route) => route.travelOrders);
    const travelOrdersWithoutPayment = travelOrders
      .map((to) => {
        const paymentTypesWithoutAmount = to.payments
          .filter((payment) => !payment.amount && payment.amount !== 0)
          .map(({ type }) => type)
          .filter(Boolean);

        if (!paymentTypesWithoutAmount.length) {
          return null;
        }

        return {
          ...to,
          paymentTypesWithoutAmount,
        };
      })
      .filter(Boolean);

    const routeStops = routes.flatMap((route) => route.routeStops);

    const createdRouteStopsWithoutFreight = routeStops.filter(
      (stop) => !stop.freights.length && stop.use && !stop.isDeleted,
    );
    const routesWithoutMandatoryStops = routes.filter((route) => {
      const createdDeliveries = route.routeStops.filter(
        (stop) => stop.type === OrderStopType.delivery && stop.use && !stop.isDeleted,
      );
      const createdPickUps = route.routeStops.filter(
        (stop) => stop.type === OrderStopType.pickup && stop.use && !stop.isDeleted,
      );

      return !createdDeliveries.length || !createdPickUps.length;
    });

    const pickUpFreights = routeStops
      .filter((stop) => stop.type === OrderStopType.pickup)
      .flatMap((stop) => stop.freights);
    const deliveriesFreights = routeStops
      .filter((stop) => stop.type === OrderStopType.delivery)
      .flatMap((stop) => stop.freights);
    const deliveryFreightsSet = new Set(deliveriesFreights);
    const freightsMismatch = pickUpFreights.some((f) => !deliveryFreightsSet.has(f));

    const routesNeededDispatch = dispatchRequired ? routes.filter((route) => !route.isDispatched) : undefined;

    return [
      {
        disabled: Boolean(travelOrdersWithoutPayment.length),
        reason: travelOrdersWithoutPayment
          ?.map((item) => {
            return item?.paymentTypesWithoutAmount.map((paymentType) => {
              return `${t("trip.add-rate-for-to", { rateType: TravelOrderPaymentTypeLabel[paymentType!] })} ${item?.name}`;
            });
          })
          .join("\n"),
      },
      {
        disabled: Boolean(routesWithoutMandatoryStops.length),
        reason: routesWithoutMandatoryStops
          .map((route) => `${t("trip.add-at-least-one-pickup-delivery-route")} #${getRoutePosition(route)}`)
          .join("\n"),
      },
      {
        disabled: Boolean(createdRouteStopsWithoutFreight.length),
        reason: [
          t("trip.add-route-freight"),
          ...createdRouteStopsWithoutFreight.map(
            (stop) => `${OrderStopTypeMap[stop.type]} #${stop.number} ${t("general.stop").toLowerCase()}`,
          ),
        ].join("\n"),
      },
      {
        disabled: freightsMismatch,
        reason: t("trip.each-freight-pick-up-delivery"),
      },
      {
        disabled: Boolean(routesNeededDispatch?.length),
        reason:
          routesNeededDispatch
            ?.map((route) => `${t("general.route")} #${getRoutePosition(route)} ${t("trip.not-dispatched")}`)
            .join("\n") || "",
      },
    ];
  }

  function createAddCheckCallDisableRules(
    confirmDispatch: boolean,
    nextStop: MaybeRefOrGetter<RouteStopDto | undefined>,
    routeStatus: RouteStatus,
  ) {
    const nextStopRaw = toValue(nextStop);
    const { hasAccess } = useBaseView();

    return [
      {
        disabled: !hasAccess(DispatcherPermissionValue.EditOrCreateCheckCalls),
        reason: t("permission.create-check-call"),
      },
      {
        disabled: !confirmDispatch,
        reason: t("trip.check-call-not-confirmed"),
      },
      {
        disabled: () => {
          if (!nextStopRaw) {
            return false;
          }

          const nextStopDisabledStatuses = [RouteStopStatus.waitingForLoading, RouteStopStatus.waitingForGoodToGo];

          return nextStopDisabledStatuses.includes(nextStopRaw.status);
        },
        reason: t("trip.check-call-driver-checked-in"),
      },
      {
        disabled: routeStatus !== RouteStatus.inProgress,
        reason: t("trip.check-call-route-not-in-progress"),
      },
    ];
  }

  function createAddTruckToRouteDisableRules(route: MaybeRefOrGetter<TripRoute>): DisabledRule[] {
    const routeRaw = toValue(route);
    const hasAtLeastOneActivePickUp = routeRaw.routeStops.some(
      (routeStop) => routeStop.use && routeStop.type === OrderStopType.pickup,
    );
    return [
      {
        disabled: !hasAtLeastOneActivePickUp,
        reason: t("trip.add-at-least-one-pickup"),
      },
    ];
  }

  return {
    createTripDispatchDisableRules,
    createAddCheckCallDisableRules,
    createAddTruckToRouteDisableRules,
  };
}
